import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Flex, Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { CompanySettings } from 'common/hooks/useCompanySettings';
import Button from 'components/_core/Button';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';
import {
  DEFAULT_LATE_MAX_DAYS_TYPE,
  MINIMUM_LATE_FEE_FALLBACK,
} from 'features/Work/constants';
import { useCachedInvoice } from 'features/Work/pages/Invoices/components/FormLayout/hooks/useCachedInvoice';
import { useCachedQuote } from 'features/Work/pages/Quotes/components/FormLayout/hooks/useCachedQuote';

import Section from '../../../Section';
import IssuerEmailCard from './components/IssuerEmailCard';
import LateMaxDays from './components/LateMaxDays';
import LegalMentionsCard from './components/LegalMentionsCard';
import PaymentLeeFee from './components/PaymentLeeFee';
import QuoteValidityDays from './components/QuoteValidityDays';
import formatUpdateMutationInput from './helpers/formatUpdateMutationInput';
import useUpdateInvoicePresets from './hooks/useUpdateInvoicePresets';
import {
  getInvoicePresetsFormSchema,
  InvoicePresetsFormSchema,
} from './invoicePresetsForm.schema';
import locales from './locales';

const getDefaultValues = (
  settings?: CompanySettings,
): InvoicePresetsFormSchema => ({
  issuerEmail: settings?.invoicingDefaultIssuerEmail ?? '',
  issuerEmailEnabled: Boolean(settings?.invoicingDefaultIssuerEmail),
  lateMaxDaysOther: settings?.invoicingDefaultLateMaxDaysOther ?? null,
  lateMaxDaysType:
    settings?.invoicingDefaultLateMaxDaysType ?? DEFAULT_LATE_MAX_DAYS_TYPE,
  legalMentions: settings?.invoicingDefaultCustomLegalMentions ?? '',
  legalMentionsEnabled: Boolean(settings?.invoicingDefaultCustomLegalMentions),
  paymentLateFee:
    settings?.invoicingDefaultLateFeeFloatPercentage ??
    MINIMUM_LATE_FEE_FALLBACK,
  quoteValidityDays: settings?.invoicingDefaultQuoteValidityDays ?? null,
  quoteValidityDaysEnabled: Boolean(
    settings?.invoicingDefaultQuoteValidityDays,
  ),
});

interface OptionsSectionProps {
  settings?: CompanySettings;
  minLatePaymentFee?: number;
}

const OptionsSection: FC<OptionsSectionProps> = ({
  minLatePaymentFee = MINIMUM_LATE_FEE_FALLBACK,
  settings,
}) => {
  const { showToast } = useToast();
  const { clearCachedInvoice } = useCachedInvoice();
  const { clearCachedQuote } = useCachedQuote();

  const [updateInvoicePresets, { loading }] = useUpdateInvoicePresets();

  const { control, formState, getValues, reset, trigger, watch } =
    useForm<InvoicePresetsFormSchema>({
      defaultValues: getDefaultValues(settings),
      mode: 'onChange',
      resolver: zodResolver(
        getInvoicePresetsFormSchema({
          minLatePaymentFee,
        }),
      ),
    });

  useEffect(() => {
    reset(getDefaultValues(settings));
  }, [settings, reset]);

  const isLegalMentionsEnabled = watch('legalMentionsEnabled');
  const isIssuerEmailEnabled = watch('issuerEmailEnabled');
  const isQuoteValidityDaysEnabled = watch('quoteValidityDaysEnabled');

  const onSubmit = async () => {
    const input = formatUpdateMutationInput(getValues());
    const { errors } = await updateInvoicePresets(input);

    if (errors) {
      showToast({ message: locales.errorMessage, type: 'error' });
      return;
    }

    clearCachedInvoice();
    clearCachedQuote();
    showToast({ message: locales.successMessage, type: 'success' });
  };

  return (
    <Section title={locales.title}>
      <Text variant="secondary">{locales.description}</Text>

      <SunshineCard>
        <Stack spacing="space-24">
          <QuoteValidityDays
            control={control}
            isEnabled={isQuoteValidityDaysEnabled}
            onCheckChange={() => trigger('quoteValidityDays')}
          />

          <LateMaxDays control={control} />

          <PaymentLeeFee
            control={control}
            minLatePaymentFee={minLatePaymentFee}
          />

          <LegalMentionsCard
            control={control}
            isEnabled={isLegalMentionsEnabled}
            onCheckChange={() => trigger('legalMentions')}
          />

          <IssuerEmailCard
            control={control}
            isEnabled={isIssuerEmailEnabled}
            onCheckChange={() => trigger('issuerEmail')}
          />
        </Stack>

        <Flex justifyContent="flex-end" marginTop="space-32">
          <Button
            icon="save"
            iconPosition="left"
            isDisabled={!formState.isValid}
            isLoading={loading}
            onClick={onSubmit}
          >
            {locales.save}
          </Button>
        </Flex>
      </SunshineCard>
    </Section>
  );
};

export default OptionsSection;
